import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Plus, LogOut, Users, Shuffle, ChevronDown, ChevronUp, Pencil, Check, X, Zap, KeyRound, RefreshCw, BarChart2, Play, Clock, Trophy, Radio, Square, UserPlus, Swords } from "lucide-react";

export default function AdminTournaments() {
  const navigate = useNavigate();
  const adminEmail = localStorage.getItem("proctorly_admin_email") || "";

  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [renaming, setRenaming] = useState({}); // { [id]: currentEditValue }
  const [renameSaving, setRenameSaving] = useState({});

  // registered users panel
  const [showUsers, setShowUsers] = useState(false);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // seeding state per tournament id
  const [seeding, setSeeding] = useState({});
  const [provisioning, setProvisioning] = useState(false);
  const [goingLive, setGoingLive] = useState({});  // { [tournamentId]: true }

  // add-player panel: { [tournamentId]: { open, phone, adding } }
  const [addPlayerPanel, setAddPlayerPanel] = useState({});
  // assign-match modal: null | { tournamentId, matchNum, players: [] }
  const [assignModal, setAssignModal] = useState(null);
  const [assignP1, setAssignP1] = useState("");
  const [assignP2, setAssignP2] = useState("");
  const [assigning, setAssigning] = useState(false);
  // tournament players cache: { [tournamentId]: [...players] }
  const [tournamentPlayers, setTournamentPlayers] = useState({});

  // event code
  const [eventCode, setEventCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);

  // player stats
  const [playerStats, setPlayerStats] = useState([]);
  const [showStats, setShowStats] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // all pending matches panel
  const [showMatches, setShowMatches] = useState(true);
  const [allMatches, setAllMatches] = useState([]); // [{tournamentId, tournamentName, level, slot}]
  const [startingMatch, setStartingMatch] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [stoppingAll, setStoppingAll] = useState(false);
  const [resettingDb, setResettingDb] = useState(false);

  const LEVEL_LABELS = { L0: "Quarter Final", L1: "Semi Final", L2: "Final", L3: "Bronze" };

  const loadAllMatches = async () => {
    setLoadingMatches(true);
    try {
      const r = await api.tournamentList();
      const tournaments = r.tournaments || [];
      const matches = [];
      // Fetch bracket details for active + live + registration_open tournaments
      for (const t of tournaments.filter((t) => ["active", "live", "registration_open"].includes(t.status))) {
        try {
          const td = await api.tournamentGet(t.id);
          const bracket = td.bracket || {};
          const l0Slots = bracket.L0 || [];
          const l1Slots = bracket.L1 || [];
          const l0AllDone = l0Slots.length > 0 && l0Slots.every((s) => s.status === "done");
          const l1AllDone = l1Slots.length > 0 && l1Slots.every((s) => s.status === "done");

          for (const [level, slots] of Object.entries(bracket)) {
            for (const slot of slots) {
              if (slot.p1_pid || slot.p2_pid || level === "L0") {
                // level-gate: L1 locked until L0 done; L2/L3 locked until L1 done
                const levelLocked =
                  (level === "L1" && !l0AllDone) ||
                  ((level === "L2" || level === "L3") && !l1AllDone);
                matches.push({
                  tournamentId: td.id,
                  tournamentName: td.name,
                  level,
                  levelLabel: LEVEL_LABELS[level] || level,
                  slot,
                  levelLocked,
                });
              }
            }
          }
        } catch { /* skip failed tournaments */ }
      }
      setAllMatches(matches);
    } catch { /* ignore */ } finally {
      setLoadingMatches(false);
    }
  };

  const resetDb = async () => {
    if (!window.confirm("⚠️ RESET DATABASE?\n\nThis will permanently delete ALL players, tournaments, matches, and game data.\n\nAdmin sessions and event codes will be preserved.\n\nThis cannot be undone. Are you sure?")) return;
    if (!window.confirm("Last chance — are you absolutely sure you want to wipe everything?")) return;
    setResettingDb(true);
    try {
      const r = await api.adminResetDb();
      toast.success(r.message || "Database reset complete.");
      loadTournaments();
      loadAllMatches();
      loadRegisteredUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setResettingDb(false);
    }
  };

  const stopAllMatches = async () => {
    if (!window.confirm("Stop ALL currently active matches? Players will be returned to a pending state.")) return;
    setStoppingAll(true);
    try {
      const r = await api.adminStopAllMatches();
      toast.success(r.message || `Stopped ${r.stopped} match(es).`);
      loadAllMatches();
      loadTournaments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop matches.");
    } finally {
      setStoppingAll(false);
    }
  };

  const startMatchFromList = async (tournamentId, level, match_num, tournamentName) => {
    const key = `${tournamentId}_${level}_${match_num}`;
    setStartingMatch(key);
    try {
      const r = await api.tournamentStartMatch(tournamentId, level, match_num);
      toast.success(`Match #${match_num} started! Players notified.`);
      if (!r.bye && r.room_id) {
        const t2 = await api.tournamentGet(tournamentId);
        const slots = t2.bracket[level] || [];
        const slot = slots.find((s) => s.m === match_num);
        if (slot && slot.p1 && slot.p2) {
          sessionStorage.setItem("proctorly_battle", JSON.stringify({
            player1: { name: slot.p1.name, email: slot.p1.email || "", phone: slot.p1.phone || "", avatar: slot.p1.avatar || {} },
            player2: { name: slot.p2.name, email: slot.p2.email || "", phone: slot.p2.phone || "", avatar: slot.p2.avatar || {} },
            room_id: r.room_id, role: "host",
            tournament: { id: tournamentId, name: tournamentName, level, match_num },
          }));
          navigate("/battle");
        }
      }
      await loadAllMatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start match");
    } finally { setStartingMatch(null); }
  };

  const goLive = async (tournamentId, tournamentName) => {
    if (!window.confirm(`Make "${tournamentName}" LIVE now?\n\nThis will:\n• Set tournament status to LIVE\n• Email all registered players to come to the Kiba Labs booth\n\nThis action CANNOT be undone.`)) return;
    setGoingLive((prev) => ({ ...prev, [tournamentId]: true }));
    try {
      const r = await api.tournamentGoLive(tournamentId);
      toast.success(`🔴 Tournament is LIVE! ${r.emails_queued} notification email(s) queued.`);
      await load();
      await loadAllMatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to go live");
    } finally {
      setGoingLive((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  // ── Add player to tournament ───────────────────────────────────────────────
  const openAddPlayer = async (tournamentId) => {
    setAddPlayerPanel((p) => ({ ...p, [tournamentId]: { open: true, phone: "", adding: false } }));
    // Refresh registered users list if not loaded
    if (!registeredUsers.length) loadRegisteredUsers();
  };

  const addPlayerToTournament = async (tournamentId, phone) => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    setAddPlayerPanel((p) => ({ ...p, [tournamentId]: { ...p[tournamentId], adding: true } }));
    try {
      const r = await api.adminAddPlayerToTournament(tournamentId, phone.trim());
      toast.success(`${r.player.name} added to tournament (${r.current_count}/8)`);
      // Refresh tournament players cache
      setTournamentPlayers((prev) => ({
        ...prev,
        [tournamentId]: [...(prev[tournamentId] || []), r.player],
      }));
      setAddPlayerPanel((p) => ({ ...p, [tournamentId]: { open: true, phone: "", adding: false } }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add player");
      setAddPlayerPanel((p) => ({ ...p, [tournamentId]: { ...p[tournamentId], adding: false } }));
    }
  };

  // ── Assign match slot ─────────────────────────────────────────────────────
  const openAssignModal = async (tournamentId, matchNum) => {
    setAssignP1(""); setAssignP2("");
    // Load fresh tournament detail to get current players list
    try {
      const td = await api.tournamentGet(tournamentId);
      setTournamentPlayers((prev) => ({ ...prev, [tournamentId]: td.players || [] }));
      setAssignModal({ tournamentId, matchNum, players: td.players || [] });
    } catch {
      toast.error("Failed to load tournament players");
    }
  };

  const submitAssignMatch = async () => {
    if (!assignModal) return;
    if (!assignP1 || !assignP2) { toast.error("Select both P1 and P2"); return; }
    if (assignP1 === assignP2) { toast.error("P1 and P2 must be different players"); return; }
    setAssigning(true);
    try {
      const r = await api.adminAssignMatch(assignModal.tournamentId, assignModal.matchNum, assignP1, assignP2);
      toast.success(`Match #${r.match_num} assigned: ${r.p1.name} vs ${r.p2.name}`);
      setAssignModal(null);
      await loadAllMatches();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign match");
    } finally { setAssigning(false); }
  };

  const load = () =>
    api.tournamentList().then((r) => setList(r.tournaments || [])).catch(() => {});

  const loadEventCode = () =>
    api.adminGetEventCode().then((r) => setEventCode(r)).catch(() => {});

  const generateEventCode = async () => {
    setGeneratingCode(true);
    try {
      const r = await api.adminGenerateEventCode();
      setEventCode({ active: true, code: r.code, expires_at: r.expires_at });
      toast.success(`Event code ${r.code} generated${r.emailed_to?.length ? ` · emailed to ${r.emailed_to.join(", ")}` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate event code");
    } finally { setGeneratingCode(false); }
  };

  const loadPlayerStats = async () => {
    setLoadingStats(true);
    try {
      const r = await api.adminPlayerStats();
      setPlayerStats(r.stats || []);
    } catch (e) {
      toast.error("Failed to load player stats");
    } finally { setLoadingStats(false); }
  };

  const toggleStats = () => {
    const next = !showStats;
    setShowStats(next);
    if (next && playerStats.length === 0) loadPlayerStats();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadEventCode(); loadAllMatches(); }, []);

  const openCreateForm = async () => {
    setCreating(true);
    try {
      const r = await api.adminNextTournamentName();
      setName(r.name);
    } catch (_) {
      setName("Proctorly Premier League (PPL) - 01");
    }
  };

  const loadRegisteredUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const r = await api.adminRegisteredUsers();
      setRegisteredUsers(r.users || []);
    } catch (e) {
      toast.error("Failed to load registered users");
    } finally { setLoadingUsers(false); }
  };

  const toggleUsers = () => {
    const next = !showUsers;
    setShowUsers(next);
    if (next && registeredUsers.length === 0) loadRegisteredUsers();
  };

  const createTournament = async () => {
    if (!name.trim()) { toast.error("Give the tournament a name"); return; }
    setSubmitting(true);
    try {
      const t = await api.tournamentCreate({ name });
      toast.success("Tournament created! Seed players from the detail page.");
      await load();
      navigate(`/admin/tournament/${t.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setSubmitting(false); }
  };

  const seedFromRegistered = async (tournamentId, count) => {
    setSeeding((s) => ({ ...s, [tournamentId]: true }));
    try {
      await api.tournamentSeedFromRegistered(tournamentId, count);
      toast.success(`${count} players randomly assigned!`);
      await load();
      navigate(`/admin/tournament/${tournamentId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed players");
    } finally {
      setSeeding((s) => ({ ...s, [tournamentId]: false }));
    }
  };

  const startRename = (t) => setRenaming((r) => ({ ...r, [t.id]: t.name }));
  const cancelRename = (id) => setRenaming((r) => { const c = { ...r }; delete c[id]; return c; });
  const saveRename = async (id) => {
    const name = (renaming[id] || "").trim();
    if (!name) return;
    setRenameSaving((s) => ({ ...s, [id]: true }));
    try {
      await api.tournamentRename(id, name);
      toast.success("Renamed!");
      cancelRename(id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    } finally { setRenameSaving((s) => { const c = { ...s }; delete c[id]; return c; }); }
  };

  const autoProvision = async () => {
    setProvisioning(true);
    try {
      const r = await api.adminAutoProvision();
      if (r.created === 0) {
        toast.info(r.message);
      } else {
        toast.success(r.message);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-provision failed");
    } finally { setProvisioning(false); }
  };

  const logout = async () => {
    try { await api.adminLogout(); } catch (_) {}
    localStorage.removeItem("proctorly_admin_token");
    localStorage.removeItem("proctorly_admin_email");
    navigate("/admin/login");
  };

  const pendingTournaments = list.filter((t) => t.status === "pending" || t.status === "registration_open");
  const liveTournaments = list.filter((t) => t.status === "live");
  const activeTournaments = list.filter((t) => t.status === "active");
  const completedTournaments = list.filter((t) => t.status === "completed");

  // Build grouped match data
  const byTournament = {};
  for (const m of allMatches) {
    if (!byTournament[m.tournamentId]) {
      byTournament[m.tournamentId] = { name: m.tournamentName, id: m.tournamentId, matches: [] };
    }
    byTournament[m.tournamentId].matches.push(m);
  }
  const tGroups = Object.values(byTournament);
  const readyCount = allMatches.filter((m) => m.slot.status === "pending" || m.slot.status === "waiting_admin").length;
  const liveCount  = allMatches.filter((m) => m.slot.status === "active").length;

  const T_STATUS = {
    active:            { label: "LIVE",              dot: "bg-[color:var(--kl-red)] animate-pulse", color: "text-[color:var(--kl-red)]",  border: "border-[color:var(--kl-red)]/40", bg: "rgba(230,57,70,0.06)" },
    live:              { label: "🔴 LIVE",           dot: "bg-[color:var(--kl-red)] animate-pulse", color: "text-[color:var(--kl-red)]",  border: "border-[color:var(--kl-red)]/60", bg: "rgba(230,57,70,0.10)" },
    registration_open: { label: "REGISTRATION OPEN", dot: "bg-yellow-400",                          color: "text-yellow-400",              border: "border-yellow-400/30",            bg: "rgba(250,204,21,0.04)" },
    pending:           { label: "PENDING",            dot: "bg-white/20",                            color: "text-white/40",                border: "border-white/10",                 bg: "rgba(255,255,255,0.02)" },
    completed:         { label: "DONE",               dot: "bg-white/20",                            color: "text-white/20",                border: "border-white/8",                  bg: "rgba(255,255,255,0.01)" },
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* ── Ambient glow ── */}
      <div className="fixed top-0 right-0 w-[700px] h-[700px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(230,57,70,0.8), transparent 60%)" }} />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 py-10">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-kl text-[11px] uppercase tracking-[0.35em] text-[color:var(--kl-red)]">
            <span className="kl-accent-line mr-3" />ADMIN · MATCH COMMAND CENTER
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/mock")} className="btn-ghost-dark text-xs flex items-center gap-1.5"
              style={{ background: "rgba(230,57,70,0.12)", borderColor: "rgba(230,57,70,0.3)", color: "#e63946" }}>
              🎯 Mock Interviews
            </button>
            <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30 hidden md:block">{adminEmail}</span>
            <button onClick={logout} className="btn-ghost-dark text-xs flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h1 className="font-display text-5xl md:text-7xl xl:text-8xl uppercase leading-[0.88] tracking-tight mt-3">
            Run a<br /><span className="text-[color:var(--kl-red)]">tournament</span>.
          </h1>

          {/* Live stats pills */}
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {liveCount > 0 && (
              <div className="flex items-center gap-2 rounded-full px-4 py-2 border border-[color:var(--kl-red)]/40"
                style={{ background: "rgba(230,57,70,0.12)" }}>
                <Radio className="w-3.5 h-3.5 text-[color:var(--kl-red)] animate-pulse" />
                <span className="font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-red)]">{liveCount} live now</span>
              </div>
            )}
            {readyCount > 0 && (
              <div className="flex items-center gap-2 rounded-full px-4 py-2 border border-yellow-400/30"
                style={{ background: "rgba(250,204,21,0.08)" }}>
                <Clock className="w-3.5 h-3.5 text-yellow-400" />
                <span className="font-mono-kl text-xs uppercase tracking-widest text-yellow-400">{readyCount} ready to start</span>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-full px-4 py-2 border border-white/10"
              style={{ background: "rgba(255,255,255,0.03)" }}>
              <Trophy className="w-3.5 h-3.5 text-white/40" />
              <span className="font-mono-kl text-xs uppercase tracking-widest text-white/40">{list.length} tournaments</span>
            </div>
          </div>
        </div>

        {/* ── Top toolbar: event code + actions ── */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {/* Event code */}
          <div className="rounded-xl border border-white/10 p-5 flex items-center justify-between gap-4"
            style={{ background: "rgba(255,255,255,0.02)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(230,57,70,0.15)" }}>
                <KeyRound className="w-4 h-4 text-[color:var(--kl-red)]" />
              </div>
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-0.5">Event Access Code</div>
                {eventCode?.active ? (
                  <span className="font-display text-3xl tracking-[0.3em] text-white">{eventCode.code}</span>
                ) : (
                  <span className="font-mono-kl text-sm text-white/30">No active code</span>
                )}
                {eventCode?.expires_at && (
                  <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/25 mt-0.5">
                    Expires {new Date(eventCode.expires_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
            <button onClick={generateEventCode} disabled={generatingCode}
              className="btn-submit text-xs shrink-0 flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${generatingCode ? "animate-spin" : ""}`} />
              {generatingCode ? "Generating…" : "New Code"}
            </button>
          </div>

          {/* Create / Auto-provision */}
          {!creating ? (
            <div className="rounded-xl border border-white/10 p-5 flex items-center justify-between gap-4"
              style={{ background: "rgba(255,255,255,0.02)" }}>
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-1">Tournaments</div>
                <div className="font-display text-lg uppercase text-white">{list.length} total · {liveTournaments.length + activeTournaments.length} live · {pendingTournaments.length} open</div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <button onClick={openCreateForm} className="btn-submit text-sm" data-testid="new-tournament">
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
                <button onClick={autoProvision} disabled={provisioning}
                  className="btn-ghost-dark text-sm flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-yellow-400" />
                  {provisioning ? "…" : "Auto-Provision"}
                </button>
                <button onClick={resetDb} disabled={resettingDb}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all border border-red-800/60 text-red-400 hover:bg-red-900/30 disabled:opacity-40"
                  title="Wipe all test data (players, matches, tournaments). Admin sessions preserved.">
                  <Square className="w-3.5 h-3.5 fill-current" />
                  {resettingDb ? "Resetting…" : "Reset DB"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[color:var(--kl-red)]/30 p-5"
              style={{ background: "rgba(230,57,70,0.04)" }} data-testid="create-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-red)]">New tournament</div>
                <button onClick={() => { setCreating(false); setName(""); }} className="text-white/40 hover:text-white transition">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2">
                <input className="input-dark flex-1 text-sm" placeholder="Tournament name…"
                  value={name} onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createTournament()}
                  data-testid="tournament-name" autoFocus />
                <button onClick={createTournament} disabled={submitting} className="btn-submit text-sm shrink-0" data-testid="create-tournament">
                  {submitting ? "…" : <><Check className="w-3.5 h-3.5" /> Create</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Match Management — HERO section ── */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
                <span className="kl-accent-line mr-3" />Match Management
              </div>
              <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight mt-1 text-white">
                All Active Matches
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={loadAllMatches} title="Refresh"
                className="btn-ghost-dark text-xs flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingMatches ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={stopAllMatches}
                disabled={stoppingAll}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all
                  bg-red-600 hover:bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Square className="w-3.5 h-3.5 fill-current" />
                {stoppingAll ? "Stopping…" : "Stop All Active"}
              </button>
            </div>
          </div>

          {loadingMatches && (
            <div className="flex items-center gap-3 text-white/40 py-6">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="font-mono-kl text-xs uppercase tracking-widest">Loading matches…</span>
            </div>
          )}

          {!loadingMatches && allMatches.length === 0 && (
            <div className="rounded-xl border border-white/8 p-10 text-center" style={{ background: "rgba(255,255,255,0.01)" }}>
              <Play className="w-8 h-8 text-white/15 mx-auto mb-3" />
              <div className="font-mono-kl text-xs uppercase tracking-widest text-white/30">No matches yet</div>
              <p className="text-white/25 text-sm mt-1">Players register for battle to get auto-assigned to a tournament.</p>
            </div>
          )}

          {!loadingMatches && tGroups.map((tGroup) => {
            const tReady = tGroup.matches.filter((m) => m.slot.status === "pending" || m.slot.status === "waiting_admin").length;
            const tLive  = tGroup.matches.filter((m) => m.slot.status === "active").length;
            return (
              <div key={tGroup.id} className="mb-10">
                {/* Tournament group header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    {tLive > 0 && <span className="w-2 h-2 rounded-full bg-[color:var(--kl-red)] animate-pulse" />}
                    {tReady > 0 && tLive === 0 && <span className="w-2 h-2 rounded-full bg-yellow-400" />}
                    <Link to={`/admin/tournament/${tGroup.id}`}
                      className="font-display text-xl md:text-2xl uppercase text-white hover:text-[color:var(--kl-red)] transition">
                      {tGroup.name}
                    </Link>
                  </div>
                  {tLive > 0 && (
                    <span className="font-mono-kl text-[8px] uppercase tracking-widest text-[color:var(--kl-red)] border border-[color:var(--kl-red)]/40 rounded-full px-2 py-0.5"
                      style={{ background: "rgba(230,57,70,0.1)" }}>
                      {tLive} live
                    </span>
                  )}
                  {tReady > 0 && (
                    <span className="font-mono-kl text-[8px] uppercase tracking-widest text-yellow-400 border border-yellow-400/30 rounded-full px-2 py-0.5"
                      style={{ background: "rgba(250,204,21,0.06)" }}>
                      {tReady} ready
                    </span>
                  )}
                  <div className="h-px flex-1 bg-white/10" />
                  <Link to={`/admin/tournament/${tGroup.id}`}
                    className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 hover:text-white transition flex items-center gap-1 shrink-0">
                    View bracket <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>

                {/* Match rows */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  {tGroup.matches.map(({ level, levelLabel, slot, levelLocked }) => {
                    const hasBothPlayers = !!(slot.p1_pid && slot.p2_pid);
                    const isPending = !levelLocked && hasBothPlayers && (slot.status === "pending" || slot.status === "waiting_admin");
                    const isActive  = slot.status === "active";
                    const isDone    = slot.status === "done";
                    const isLocked  = levelLocked && !isActive && !isDone;
                    const key = `${tGroup.id}_${level}_${slot.m}`;

                    return (
                      <div key={key}
                        className={`rounded-xl border flex gap-0 overflow-hidden transition ${
                          isActive  ? "border-[color:var(--kl-red)]/50" :
                          isPending ? "border-yellow-400/30" :
                          isLocked  ? "border-white/5 opacity-40" :
                          "border-white/8"
                        }`}
                        style={{
                          background: isActive ? "rgba(230,57,70,0.08)" : isPending ? "rgba(250,204,21,0.04)" : "rgba(255,255,255,0.02)",
                          boxShadow: isActive ? "0 0 24px rgba(230,57,70,0.15)" : "none",
                        }}>

                        {/* Left accent strip */}
                        <div className={`w-1 shrink-0 ${isActive ? "bg-[color:var(--kl-red)]" : isPending ? "bg-yellow-400" : isLocked ? "bg-white/5" : "bg-white/10"}`} />

                        <div className="flex items-center gap-4 px-5 py-4 flex-1 min-w-0">
                          {/* Match info */}
                          <div className="shrink-0 text-center w-14">
                            <div className="font-mono-kl text-[7px] uppercase tracking-widest text-white/30">Match</div>
                            <div className="font-display text-2xl text-white leading-none">#{slot.m}</div>
                            <div className={`font-mono-kl text-[7px] uppercase tracking-widest mt-0.5 ${
                              isActive ? "text-[color:var(--kl-red)]" : isPending ? "text-yellow-400/80" : "text-white/20"
                            }`}>{levelLabel}</div>
                          </div>

                          {/* P1 */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-12 rounded-lg overflow-hidden shrink-0 border border-white/10"
                              style={{ background: "rgba(255,255,255,0.05)" }}>
                              {slot.p1?.avatar?.img ? (
                                <img src={slot.p1.avatar.img} alt={slot.p1?.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/20 font-display text-lg">
                                  {(slot.p1?.name || "?")[0]}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-display text-base uppercase text-white truncate">{slot.p1?.name || "TBD"}</div>
                              <div className="font-mono-kl text-[9px] text-white/30 truncate">{slot.p1?.phone || "—"}</div>
                            </div>
                          </div>

                          {/* VS */}
                          <div className="shrink-0 font-display text-sm text-white/20">VS</div>

                          {/* P2 */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-12 rounded-lg overflow-hidden shrink-0 border border-white/10"
                              style={{ background: "rgba(255,255,255,0.05)" }}>
                              {slot.p2?.avatar?.img ? (
                                <img src={slot.p2.avatar.img} alt={slot.p2?.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/20 font-display text-lg">
                                  {(slot.p2?.name || "?")[0]}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-display text-base uppercase text-white truncate">{slot.p2?.name || "TBD"}</div>
                              <div className="font-mono-kl text-[9px] text-white/30 truncate">{slot.p2?.phone || "—"}</div>
                            </div>
                          </div>

                          {/* Action */}
                          <div className="shrink-0 flex flex-col items-end gap-2 ml-2">
                            {isActive && (
                              <span className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--kl-red)] animate-pulse" /> LIVE
                              </span>
                            )}
                            {isDone && (
                              <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/25">Done</span>
                            )}
                            {isLocked && (
                              <span className="font-mono-kl text-[8px] uppercase tracking-widest text-white/20">🔒 Locked</span>
                            )}
                            {!hasBothPlayers && !isActive && !isDone && !isLocked && (
                              <button
                                onClick={() => openAssignModal(tGroup.id, slot.m)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono-kl uppercase tracking-widest transition-all border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10"
                              >
                                <Swords className="w-3 h-3" /> Assign
                              </button>
                            )}
                            {isPending && hasBothPlayers && (
                              <button
                                onClick={() => startMatchFromList(tGroup.id, level, slot.m, tGroup.name)}
                                disabled={!!startingMatch}
                                className="btn-submit text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-60"
                                data-testid={`start-match-${tGroup.id}-${slot.m}`}
                              >
                                {startingMatch === key ? (
                                  <><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Starting</>
                                ) : (
                                  <><Play className="w-3.5 h-3.5" /> START</>
                                )}
                              </button>
                            )}
                          
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Tournament List ── */}
        {list.length > 0 && (
          <div className="mt-10 border-t border-white/8 pt-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-white/40">
                  <span className="kl-accent-line mr-3" />All Tournaments
                </div>
                <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight mt-1 text-white">
                  Tournament List
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tournament-list">
              {[...liveTournaments, ...activeTournaments, ...pendingTournaments, ...completedTournaments].map((t) => {
                const ts = T_STATUS[t.status] || T_STATUS.pending;
                const count = t.current_player_count ?? t.player_count ?? 0;
                const max   = t.max_players ?? 8;
                return (
                  <div key={t.id}
                    className={`rounded-xl border p-5 flex flex-col gap-4 transition hover:opacity-90 ${ts.border}`}
                    style={{ background: ts.bg }}
                    data-testid={`tournament-item-${t.id}`}>

                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {renaming[t.id] !== undefined ? (
                          <div className="flex items-center gap-2">
                            <input className="input-dark font-display text-lg uppercase flex-1 py-1"
                              value={renaming[t.id]}
                              onChange={(e) => setRenaming((r) => ({ ...r, [t.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter") saveRename(t.id); if (e.key === "Escape") cancelRename(t.id); }}
                              autoFocus />
                            <button onClick={() => saveRename(t.id)} disabled={renameSaving[t.id]} className="btn-submit text-xs px-2 py-1"><Check className="w-3 h-3" /></button>
                            <button onClick={() => cancelRename(t.id)} className="btn-ghost-dark text-xs px-2 py-1"><X className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1.5 group">
                            <Link to={`/admin/tournament/${t.id}`}
                              className="font-display text-xl uppercase text-white hover:text-[color:var(--kl-red)] transition leading-tight truncate">
                              {t.name}
                            </Link>
                            <button onClick={() => startRename(t)}
                              className="opacity-0 group-hover:opacity-100 transition text-white/30 hover:text-white mt-0.5 shrink-0">
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Status pill */}
                      <div className={`flex items-center gap-1.5 font-mono-kl text-[8px] uppercase tracking-widest shrink-0 ${ts.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ts.dot}`} />
                        {ts.label}
                        {t.auto_created && <span className="text-yellow-400/60 border border-yellow-400/20 rounded px-1 ml-1">Auto</span>}
                      </div>
                    </div>

                    {/* Capacity bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono-kl text-[8px] uppercase tracking-widest text-white/30">Players</span>
                        <span className="font-mono-kl text-[10px] text-white/60">{count}/{max}</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${Math.min(100, (count / max) * 100)}%`, background: count >= max ? "var(--kl-red)" : "#facc15" }} />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/admin/tournament/${t.id}`} className="btn-ghost-dark text-xs flex-1 text-center justify-center">
                          View bracket <ArrowUpRight className="w-3 h-3" />
                        </Link>
                        {(t.status === "pending" || t.status === "registration_open") && count < 8 && (
                          <button
                            onClick={() => openAddPlayer(t.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono-kl uppercase tracking-widest transition-all border border-white/20 text-white/60 hover:border-[color:var(--kl-red)]/50 hover:text-white">
                            <UserPlus className="w-3 h-3" />
                            Add Player
                          </button>
                        )}
                        {(t.status === "pending" || t.status === "registration_open") && count >= 1 && (
                          <button
                            onClick={() => goLive(t.id, t.name)}
                            disabled={goingLive[t.id]}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono-kl uppercase tracking-widest transition-all"
                            style={{ background: "rgba(230,57,70,0.15)", border: "1.5px solid var(--kl-red)", color: "var(--kl-red)" }}>
                            <Radio className="w-3 h-3" />
                            {goingLive[t.id] ? "Going Live…" : "Go Live 🔴"}
                          </button>
                        )}
                        {t.winner && (
                          <div className="font-display text-sm text-[color:var(--kl-red)] ml-auto flex items-center gap-1">
                            <Trophy className="w-3.5 h-3.5" /> {t.winner.name}
                          </div>
                        )}
                      </div>

                      {/* Add-player inline panel */}
                      {addPlayerPanel[t.id]?.open && (
                        <div className="rounded-lg border border-white/15 p-3 space-y-2"
                          style={{ background: "rgba(255,255,255,0.03)" }}>
                          <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-1">
                            Add player by phone — {count}/8 slots filled
                          </div>
                          {/* Quick search from registered users */}
                          <div className="max-h-36 overflow-y-auto space-y-1">
                            {(registeredUsers.filter((u) =>
                              !(t.players || []).some((p) => p.phone === u.phone)
                            )).slice(0, 20).map((u) => (
                              <button
                                key={u.phone}
                                onClick={() => addPlayerToTournament(t.id, u.phone)}
                                disabled={addPlayerPanel[t.id]?.adding}
                                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/10 transition disabled:opacity-40">
                                <div className="w-6 h-6 rounded-full bg-[color:var(--kl-red)]/20 flex items-center justify-center shrink-0">
                                  <span className="font-display text-xs text-[color:var(--kl-red)]">{(u.name || "?")[0]}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-display text-xs uppercase text-white truncate">{u.name}</div>
                                  <div className="font-mono-kl text-[8px] text-white/35 truncate">{u.phone}</div>
                                </div>
                                <UserPlus className="w-3 h-3 text-white/30 shrink-0" />
                              </button>
                            ))}
                            {registeredUsers.filter((u) =>
                              !(t.players || []).some((p) => p.phone === u.phone)
                            ).length === 0 && (
                              <div className="text-white/30 font-mono-kl text-[9px] text-center py-2">
                                No unassigned registered players found
                              </div>
                            )}
                          </div>
                          {/* Manual phone input fallback */}
                          <div className="flex gap-1.5 pt-1 border-t border-white/8">
                            <input
                              className="input-dark flex-1 text-xs py-1"
                              placeholder="Or type phone number…"
                              value={addPlayerPanel[t.id]?.phone || ""}
                              onChange={(e) => setAddPlayerPanel((p) => ({ ...p, [t.id]: { ...p[t.id], phone: e.target.value } }))}
                              onKeyDown={(e) => e.key === "Enter" && addPlayerToTournament(t.id, addPlayerPanel[t.id]?.phone)}
                            />
                            <button
                              onClick={() => addPlayerToTournament(t.id, addPlayerPanel[t.id]?.phone || "")}
                              disabled={addPlayerPanel[t.id]?.adding}
                              className="btn-submit text-xs px-2 py-1 shrink-0">
                              {addPlayerPanel[t.id]?.adding ? "…" : <Check className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => setAddPlayerPanel((p) => ({ ...p, [t.id]: { open: false, phone: "", adding: false } }))}
                              className="btn-ghost-dark text-xs px-2 py-1 shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/20">
                      Created {new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                );
              })}

              {/* New tournament card */}
              {!creating && (
                <button onClick={openCreateForm}
                  className="rounded-xl border border-dashed border-white/15 p-5 flex flex-col items-center justify-center gap-2 hover:border-[color:var(--kl-red)]/40 hover:bg-[rgba(230,57,70,0.04)] transition min-h-[160px]"
                  data-testid="new-tournament">
                  <Plus className="w-6 h-6 text-white/20" />
                  <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30">New Tournament</span>
                </button>
              )}
            </div>
          </div>
        )}

        {list.length === 0 && !creating && (
          <div className="mt-10 rounded-xl border border-dashed border-white/10 p-10 text-center">
            <Trophy className="w-8 h-8 text-white/15 mx-auto mb-3" />
            <div className="font-mono-kl text-xs uppercase tracking-widest text-white/30">No tournaments yet</div>
            <button onClick={openCreateForm} className="btn-submit mt-5">
              <Plus className="w-4 h-4" /> Create First Tournament
            </button>
          </div>
        )}

        {/* ── Collapsible panels: Players + Stats ── */}
        <div className="mt-10 space-y-3 border-t border-white/8 pt-8">
          {/* Player Battle Stats */}
          <div className="rounded-xl border border-white/10 overflow-hidden" style={{ background: "rgba(255,255,255,0.02)" }}>
            <button onClick={toggleStats}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-[color:var(--kl-red)]" />
                <span className="font-mono-kl text-xs uppercase tracking-widest text-white/60">
                  Player Battle Stats {playerStats.length > 0 && `· ${playerStats.length} players`}
                </span>
              </div>
              {showStats ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
            </button>
            {showStats && (
              <div className="px-6 pb-6">
                {loadingStats && <div className="text-white/40 text-sm py-3">Loading…</div>}
                {!loadingStats && playerStats.length === 0 && <div className="text-white/40 text-sm py-3">No battle data yet.</div>}
                {!loadingStats && playerStats.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-white/80">
                      <thead>
                        <tr className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 border-b border-white/8">
                          <th className="text-left py-2 pr-4">Player</th>
                          <th className="text-center py-2 px-2">Battles</th>
                          <th className="text-center py-2 px-2">Wins</th>
                          <th className="text-center py-2 px-2">Losses</th>
                          <th className="text-center py-2 px-2">Ties</th>
                          <th className="text-left py-2 pl-4">Last Battle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerStats.map((s, i) => (
                          <tr key={s.phone || i} className="border-b border-white/5 hover:bg-white/[0.03] transition">
                            <td className="py-2 pr-4">
                              <div className="font-display text-sm uppercase text-white">{s.name}</div>
                              <div className="font-mono-kl text-[9px] text-white/30">{s.phone}</div>
                            </td>
                            <td className="text-center py-2 px-2 font-display text-lg text-white">{s.battles_total || 0}</td>
                            <td className="text-center py-2 px-2 text-green-400 font-display">{s.battles_wins || 0}</td>
                            <td className="text-center py-2 px-2 text-[color:var(--kl-red)] font-display">{s.battles_losses || 0}</td>
                            <td className="text-center py-2 px-2 text-white/40">{s.battles_ties || 0}</td>
                            <td className="text-left py-2 pl-4 font-mono-kl text-[10px] text-white/35">
                              {s.last_battle_at ? new Date(s.last_battle_at).toLocaleDateString("en-IN") : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Registered Players */}
          <div className="rounded-xl border border-white/10 overflow-hidden" style={{ background: "rgba(255,255,255,0.02)" }}>
            <button onClick={toggleUsers}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[color:var(--kl-red)]" />
                <span className="font-mono-kl text-xs uppercase tracking-widest text-white/60">
                  Registered Players {registeredUsers.length > 0 && (
                    <span className="text-white ml-1">{registeredUsers.length}</span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); loadRegisteredUsers(); }}
                  disabled={loadingUsers} className="text-white/30 hover:text-white transition p-1">
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingUsers ? "animate-spin" : ""}`} />
                </button>
                {showUsers ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
              </div>
            </button>

            {showUsers && (
              <div className="px-6 pb-6">
                {loadingUsers && (
                  <div className="flex items-center gap-2 text-white/40 py-4">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span className="font-mono-kl text-xs uppercase tracking-widest">Loading players…</span>
                  </div>
                )}
                {!loadingUsers && registeredUsers.length === 0 && (
                  <div className="text-white/30 font-mono-kl text-xs uppercase tracking-widest py-4">No verified players yet.</div>
                )}
                {!loadingUsers && registeredUsers.length > 0 && (() => {
                    const GIRL_POOL = ["24-girl","25-girl","26-girl","34-girl","35-girl","36-girl","38-girl"];
                    const BOY_POOL  = ["27-boy","28-boy","29-boy","30-boy","31-boy","32-boy","33-boy","37-boy"];
                    const pickAvatar = (u, idx) => {
                      if (u.avatar?.img) return u.avatar.img;
                      const gender = (u.gender || "").toUpperCase();
                      const pool = gender === "F" ? GIRL_POOL : BOY_POOL;
                      const seed = (u.phone || "").replace(/\D/g, "").split("").reduce((a, c) => a + Number(c), idx);
                      return `/${pool[seed % pool.length]}.png`;
                    };
                    return (
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-11 2xl:grid-cols-13 gap-2 mt-2">
                    {registeredUsers.map((u, i) => {
                      const avatarImg = pickAvatar(u, i);
                      const gender = (u.gender || "").toUpperCase();
                      const initials = (u.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

                      return (
                        <div key={u.id || i}
                          className="rounded-xl border border-white/8 overflow-hidden flex flex-col hover:border-white/25 hover:scale-[1.02] transition-all duration-150 cursor-default"
                          style={{ background: "rgba(255,255,255,0.02)" }}
                          title={`${u.name} · ${u.phone || u.email || ""}`}>
                          {/* Avatar */}
                          <div className="relative w-full aspect-[3/4] overflow-hidden"
                            style={{ background: "rgba(255,255,255,0.04)" }}>
                            <img
                              src={avatarImg}
                              alt={u.name}
                              className="w-full h-full object-cover object-top"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                            {/* Fallback initials if img fails */}
                            <div className="absolute inset-0 flex items-center justify-center font-display text-2xl text-white/20 pointer-events-none">
                              {initials}
                            </div>
                            {/* Gender pill */}
                            {gender && (
                              <div className={`absolute top-1 right-1 font-mono-kl text-[7px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                gender === "F" ? "bg-pink-500/70 text-white" : "bg-blue-500/70 text-white"
                              }`}>
                                {gender}
                              </div>
                            )}
                          </div>
                          {/* Name + info */}
                          <div className="px-2 py-2">
                            <div className="font-display text-sm uppercase text-white leading-tight truncate">{u.name}</div>
                            <div className="font-mono-kl text-[8px] text-white/30 truncate mt-0.5">{u.phone || u.email || "—"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  );
                  })()}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Assign Match Modal ─────────────────────────────────────────────── */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setAssignModal(null); }}>
          <div className="w-full max-w-md rounded-2xl border border-white/15 p-6 space-y-5"
            style={{ background: "rgba(10,13,34,0.98)", boxShadow: "0 0 60px rgba(230,57,70,0.15)" }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)]">Assign Players</div>
                <div className="font-display text-2xl uppercase text-white mt-0.5">Match #{assignModal.matchNum}</div>
              </div>
              <button onClick={() => setAssignModal(null)} className="text-white/30 hover:text-white transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {assignModal.players.length < 2 ? (
              <div className="text-center py-6">
                <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-sm font-mono-kl">
                  Add at least 2 players to this tournament first before assigning a match.
                </p>
                <button onClick={() => { setAssignModal(null); openAddPlayer(assignModal.tournamentId); }}
                  className="btn-submit text-sm mt-4">
                  <UserPlus className="w-3.5 h-3.5" /> Add Players
                </button>
              </div>
            ) : (
              <>
                {["P1", "P2"].map((label, i) => {
                  const val = i === 0 ? assignP1 : assignP2;
                  const set = i === 0 ? setAssignP1 : setAssignP2;
                  const other = i === 0 ? assignP2 : assignP1;
                  return (
                    <div key={label}>
                      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-2">{label} — Player {i + 1}</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {assignModal.players.filter((p) => p.phone !== other).map((p) => (
                          <button key={p.phone}
                            onClick={() => set(p.phone)}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition ${
                              val === p.phone
                                ? "border-[color:var(--kl-red)]/60 bg-[rgba(230,57,70,0.12)]"
                                : "border-white/10 hover:bg-white/5"
                            }`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${val === p.phone ? "bg-[color:var(--kl-red)]" : "bg-white/10"}`}>
                              <span className="font-display text-sm text-white">{(p.name || "?")[0]}</span>
                            </div>
                            <div className="min-w-0">
                              <div className="font-display text-sm uppercase text-white truncate">{p.name}</div>
                              <div className="font-mono-kl text-[9px] text-white/35">{p.phone}</div>
                            </div>
                            {val === p.phone && <Check className="w-4 h-4 text-[color:var(--kl-red)] shrink-0 ml-auto" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <button
                  onClick={submitAssignMatch}
                  disabled={assigning || !assignP1 || !assignP2}
                  className="btn-submit w-full flex items-center justify-center gap-2 disabled:opacity-50">
                  {assigning ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Assigning…</>
                  ) : (
                    <><Swords className="w-4 h-4" /> Confirm Match Assignment</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
